import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Circle, AlertCircle, Loader2, ChevronRight, PenTool, RotateCcw, Send } from "lucide-react";
import { Logo, GoldRule } from "@/components/Brand";
import { CAT_LABEL } from "@/pages/staff/pages/Forms";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/forms`;

/* --------- Small helpers copied to avoid pulling staff bundle -------- */
const uid = () => crypto.randomUUID();

function isVisible(field, answers, fmap) {
    const rule = field.conditional;
    if (!rule) return true;
    const target = fmap[rule.field_id];
    if (!target || !isVisible(target, answers, fmap)) return false;
    const val = answers[rule.field_id];
    if (rule.operator === "in") return Array.isArray(val) && val.includes(rule.value);
    return String(val ?? "") === String(rule.value ?? "");
}

/* ==================== Customer Portal ==================== */
export function CustomerFormPortal() {
    const { token } = useParams();
    const [pkg, setPkg] = useState(null);
    const [error, setError] = useState(null);
    const [current, setCurrent] = useState(null);  // form_template_id being filled

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API_BASE}/${token}`);
            setPkg(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "This link is not valid.");
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    if (error) return <ErrorScreen message={error} />;
    if (!pkg) return <LoadingScreen />;

    if (current) {
        const form = pkg.forms.find((f) => f.form_template_id === current);
        return <FormFillScreen token={token} form={form} onDone={() => { setCurrent(null); load(); }} onBack={() => setCurrent(null)} snapshot={pkg.snapshot} />;
    }
    return <PackageIndex pkg={pkg} onPick={setCurrent} />;
}

function LoadingScreen() {
    return (
        <div className="min-h-screen bg-navy flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-gold animate-spin" />
        </div>
    );
}
function ErrorScreen({ message }) {
    return (
        <div className="min-h-screen bg-navy flex items-center justify-center p-6">
            <div className="w-full max-w-md text-center space-y-4" data-testid="portal-error">
                <Logo size={72} />
                <h1 className="font-serif text-white text-2xl">Link unavailable</h1>
                <p className="text-white/70 text-sm">{message}</p>
                <p className="text-white/50 text-xs">Please contact the clinic if you believe this is a mistake.</p>
            </div>
        </div>
    );
}

/* ==================== Index (list of forms) ==================== */
function PackageIndex({ pkg, onPick }) {
    const { snapshot, forms, progress } = pkg;
    const first = snapshot.customer_first_name || "there";
    const service = snapshot.service_name || "your treatment";
    const apptDate = snapshot.appointment_start
        ? new Date(snapshot.appointment_start.replace(" ", "T")).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })
        : null;
    return (
        <div className="min-h-screen bg-navy text-white pb-20" data-testid="portal-index">
            <header className="text-center pt-8 pb-6 px-4">
                <Logo size={92} />
                <GoldRule className="mt-4" />
                <h1 className="font-serif text-white text-2xl mt-3">Hello {first}</h1>
                <p className="text-white/70 text-sm mt-1">Please review and complete the forms below.</p>
                <p className="text-gold text-xs mt-2 uppercase tracking-[0.16em]" data-testid="portal-service">{service}</p>
                {apptDate && <p className="text-white/50 text-xs mt-1" data-testid="portal-date">{apptDate}</p>}
            </header>
            <main className="max-w-md mx-auto px-4 space-y-3">
                <ProgressBar completed={progress.completed} total={progress.total} />
                {forms.map((f, i) => (
                    <button key={f.form_template_id} onClick={() => !f.completed && onPick(f.form_template_id)}
                            data-testid={`portal-form-${f.form_template_id}`}
                            disabled={f.completed}
                            className={`w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-all ${f.completed ? "border-emerald-400/40 bg-emerald-500/5" : "hairline hover:border-gold/60 bg-white/[0.02]"}`}>
                        <div className="shrink-0">
                            {f.completed ? <CheckCircle2 className="h-6 w-6 text-emerald-300" /> : <Circle className="h-6 w-6 text-white/40" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-serif text-white text-lg break-words">{f.title}</div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mt-0.5">
                                {CAT_LABEL[f.category] || f.category} · {f.required ? "Required" : "Optional"} · v{f.version_number}
                                {f.completed && <span className="text-emerald-300 ml-2">Completed</span>}
                            </div>
                        </div>
                        {!f.completed && <ChevronRight className="h-5 w-5 text-gold shrink-0" />}
                    </button>
                ))}
                {progress.completed === progress.total && (
                    <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-center text-emerald-100" data-testid="portal-all-done">
                        <CheckCircle2 className="h-6 w-6 mx-auto mb-2" />
                        <p className="font-serif text-lg">Thank you</p>
                        <p className="text-xs">All forms have been received. Thank you.</p>
                    </div>
                )}
            </main>
        </div>
    );
}

function ProgressBar({ completed, total }) {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
        <div className="rounded-2xl border hairline p-3 bg-white/[0.03]" data-testid="portal-progress">
            <div className="flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-[0.14em] text-white/60">Progress</span>
                <span className="text-xs text-gold">{completed} of {total} complete</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

/* ==================== Fill screen ==================== */
function FormFillScreen({ token, form, onDone, onBack, snapshot }) {
    const [answers, setAnswers] = useState(() => ({ ...(form.draft_answers || {}) }));
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [signatureId, setSignatureId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);

    // Build id -> field map for conditional evaluation
    const allFields = useMemo(() => form.sections.flatMap((s) => s.fields || []), [form]);
    const fmap = useMemo(() => Object.fromEntries(allFields.map((f) => [f.id, f])), [allFields]);
    const hasSignatureField = useMemo(() => allFields.some((f) => f.kind === "signature"), [allFields]);

    // Autosave — debounced
    const dirty = useRef(false);
    useEffect(() => {
        if (!dirty.current) return;
        const t = setTimeout(async () => {
            try {
                await axios.put(`${API_BASE}/${token}/drafts/${form.form_template_id}`, { answers });
                setSavedAt(new Date());
                dirty.current = false;
            } catch (_) {}
        }, 900);
        return () => clearTimeout(t);
    }, [answers, token, form.form_template_id]);

    const setValue = (fid, v) => { dirty.current = true; setAnswers((prev) => ({ ...prev, [fid]: v })); };

    const submit = async () => {
        setError(null);
        // Client-side pre-check for required visible fields
        for (const f of allFields) {
            if (["section_heading", "informational_text", "auto_timestamp"].includes(f.kind)) continue;
            if (!f.required) continue;
            if (!isVisible(f, answers, fmap)) continue;
            const v = answers[f.id];
            if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || (f.kind === "confirmation" && !v)) {
                setError(`Please answer: "${f.label || f.id}"`);
                return;
            }
        }
        // Upload signature if drawn
        let sigId = signatureId;
        if (hasSignatureField) {
            if (!signatureDataUrl) { setError("Please add your signature to continue."); return; }
            if (!sigId) {
                try {
                    const r = await axios.post(`${API_BASE}/${token}/signatures`, { image_base64: signatureDataUrl });
                    sigId = r.data.signature_id;
                    setSignatureId(sigId);
                } catch (e) { setError(e?.response?.data?.detail || "Signature upload failed."); return; }
            }
        }
        setSubmitting(true);
        try {
            await axios.post(`${API_BASE}/${token}/submissions`, {
                form_template_id: form.form_template_id,
                answers,
                signature_id: sigId,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            onDone();
        } catch (e) {
            setError(e?.response?.data?.detail || "Submission failed.");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-navy text-white pb-24" data-testid="portal-fill">
            <header className="sticky top-0 z-20 bg-navy/95 backdrop-blur border-b hairline">
                <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
                    <button onClick={onBack} data-testid="portal-back" className="text-white/60 text-sm">← Back</button>
                    <div className="min-w-0 flex-1 text-center">
                        <div className="font-serif text-white text-base truncate">{form.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">v{form.version_number} · {form.required ? "Required" : "Optional"}</div>
                    </div>
                    <div className="w-10 text-right">
                        {savedAt && <span className="text-[10px] text-emerald-300">Saved</span>}
                    </div>
                </div>
            </header>
            <main className="max-w-md mx-auto px-4 py-4 space-y-6">
                {form.sections.map((s) => (
                    <section key={s.id}>
                        {s.title && <h2 className="font-serif text-white text-xl">{s.title}</h2>}
                        {s.description && <p className="text-white/60 text-xs mt-1">{s.description}</p>}
                        <div className="space-y-4 mt-3">
                            {(s.fields || []).map((f) => isVisible(f, answers, fmap) && (
                                <FieldInput key={f.id} field={f} value={answers[f.id]} onChange={(v) => setValue(f.id, v)} />
                            ))}
                        </div>
                    </section>
                ))}
                {hasSignatureField && (
                    <SignaturePad onChange={(dataUrl) => { dirty.current = true; setSignatureDataUrl(dataUrl); setSignatureId(null); }} />
                )}
                {error && (
                    <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-3 text-red-200 text-sm flex items-start gap-2" data-testid="portal-error-msg">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span>{error}</span>
                    </div>
                )}
                <button onClick={submit} disabled={submitting} data-testid="portal-submit"
                        className="w-full h-12 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Submit
                </button>
                <p className="text-[10px] text-white/40 text-center">This link is unique to your appointment. You can leave and return using the same link.</p>
            </main>
        </div>
    );
}

/* ==================== Signature Pad ==================== */
function SignaturePad({ onChange }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = c.getBoundingClientRect();
        c.width = Math.floor(rect.width * ratio);
        c.height = Math.floor(rect.height * ratio);
        const ctx = c.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.strokeStyle = "#f5e9c9";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
    }, []);

    const pos = (e) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const t = e.touches?.[0] || e;
        return { x: (t.clientX - rect.left), y: (t.clientY - rect.top) };
    };
    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        const { x, y } = pos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath(); ctx.moveTo(x, y);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const { x, y } = pos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.lineTo(x, y); ctx.stroke();
        if (empty) setEmpty(false);
    };
    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        const dataUrl = canvasRef.current.toDataURL("image/png");
        onChange(dataUrl);
    };
    const clear = () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        setEmpty(true);
        onChange(null);
    };

    return (
        <div className="rounded-2xl border hairline p-3 bg-white/[0.03]" data-testid="portal-signature">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-white/60">
                <PenTool className="h-4 w-4 text-gold" /> Please sign below
            </div>
            <div className="mt-2 rounded-xl bg-white/[0.06] border border-dashed border-white/20 relative" style={{ height: 180, touchAction: "none" }}>
                <canvas
                    ref={canvasRef}
                    onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                    onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                    data-testid="portal-signature-canvas"
                    className="w-full h-full rounded-xl"
                />
                {empty && <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-white/30 text-xs italic">Sign here</div>}
            </div>
            <div className="mt-2 flex justify-between items-center">
                <p className="text-[10px] text-white/40">Draw with your finger or mouse.</p>
                <button onClick={clear} data-testid="portal-signature-clear" className="text-xs text-white/60 inline-flex items-center gap-1 hover:text-gold">
                    <RotateCcw className="h-3 w-3" /> Clear
                </button>
            </div>
        </div>
    );
}

/* ==================== Field Input (customer-facing) ==================== */
function FieldInput({ field, value, onChange }) {
    const base = "w-full bg-transparent border hairline rounded-lg p-3 text-base text-white focus:outline-none focus:border-gold/60";
    const label = !["section_heading", "informational_text", "auto_timestamp"].includes(field.kind) && (
        <label className="block text-sm text-white/85 mb-1.5">
            {field.label}{field.required && <span className="text-gold ml-1">*</span>}
        </label>
    );
    const help = field.description ? <p className="text-[11px] text-white/45 mt-1">{field.description}</p> : null;
    switch (field.kind) {
        case "section_heading":  return <h3 className="font-serif text-white text-lg mt-2">{field.label}</h3>;
        case "informational_text": return <p className="text-white/70 text-sm leading-relaxed">{field.description || field.label}</p>;
        case "short_text":       return <div>{label}<input placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`fi-${field.id}`} />{help}</div>;
        case "long_text":        return <div>{label}<textarea rows={3} placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`fi-${field.id}`} />{help}</div>;
        case "email":            return <div>{label}<input type="email" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`fi-${field.id}`} />{help}</div>;
        case "phone":            return <div>{label}<input type="tel" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`fi-${field.id}`} />{help}</div>;
        case "date":
        case "date_of_birth":    return <div>{label}<input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`fi-${field.id}`} />{help}</div>;
        case "number":           return <div>{label}<input type="number" placeholder={field.placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`fi-${field.id}`} />{help}</div>;
        case "yes_no": return (
            <div>{label}
                <div className="flex gap-2">
                    {["yes", "no"].map((v) => (
                        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`fi-${field.id}-${v}`}
                                className={`flex-1 h-12 rounded-full border text-sm uppercase ${value === v ? "bg-gold text-navy border-gold" : "hairline text-white/80"}`}>
                            {v === "yes" ? "Yes" : "No"}
                        </button>
                    ))}
                </div>
                {help}
            </div>
        );
        case "radio": return (
            <div>{label}<div className="space-y-2">
                {(field.options || []).map((o) => (
                    <label key={o.id} className="flex items-center gap-3 text-sm text-white/90 min-h-11">
                        <input type="radio" name={field.id} checked={value === o.label} onChange={() => onChange(o.label)} className="accent-gold h-5 w-5" data-testid={`fi-${field.id}-${o.id}`} />
                        {o.label}
                    </label>
                ))}
            </div>{help}</div>
        );
        case "checkboxes": {
            const arr = Array.isArray(value) ? value : [];
            return (
                <div>{label}<div className="space-y-2">
                    {(field.options || []).map((o) => (
                        <label key={o.id} className="flex items-center gap-3 text-sm text-white/90 min-h-11">
                            <input type="checkbox" checked={arr.includes(o.label)} onChange={() => onChange(arr.includes(o.label) ? arr.filter((x) => x !== o.label) : [...arr, o.label])} className="accent-gold h-5 w-5" data-testid={`fi-${field.id}-${o.id}`} />
                            {o.label}
                        </label>
                    ))}
                </div>{help}</div>
            );
        }
        case "dropdown": return (
            <div>{label}
                <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${base} bg-navy`} data-testid={`fi-${field.id}`}>
                    <option value="">{field.placeholder || "Select…"}</option>
                    {(field.options || []).map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
                </select>
                {help}
            </div>
        );
        case "confirmation": return (
            <label className="flex items-start gap-3 text-sm text-white/90">
                <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-gold h-5 w-5 mt-1" data-testid={`fi-${field.id}`} />
                <span>{field.label}{field.required && <span className="text-gold ml-1">*</span>}{field.description && <span className="block text-white/50 text-xs mt-1">{field.description}</span>}</span>
            </label>
        );
        case "initials": return <div>{label}<input maxLength={4} placeholder="AT" value={value || ""} onChange={(e) => onChange(e.target.value.toUpperCase())} className={`${base} w-32 tracking-[0.4em] text-center`} data-testid={`fi-${field.id}`} />{help}</div>;
        case "signature": return null;  // signature captured once at bottom of form
        case "auto_timestamp": return <p className="text-[11px] text-white/40">Auto-recorded when you submit.</p>;
        default: return null;
    }
}

export default CustomerFormPortal;
